import { useMemo, useState } from "react";
import { FunnelSimple, X } from "@phosphor-icons/react";
import { useEscClose } from "@/lib/useEscClose";

export const EMPTY_FILTERS = { customer: "", silk: "", cupColor: "", sku: "", overdueOnly: false, stoppedDays: "", deadlineStart: "", deadlineEnd: "" };

/**
 * Modal de filtro avançado do Kanban. As opções são auto-populadas a partir
 * dos pedidos ativos carregados no board (clientes, cores, SKUs).
 */
export default function KanbanFilterModal({ orders, filters, onApply, onClose }) {
  const [f, setF] = useState({ ...EMPTY_FILTERS, ...filters });
  useEscClose(onClose);

  const opts = useMemo(() => {
    const customers = new Set(), silks = new Set(), cupColors = new Set(), skus = new Set();
    for (const o of orders) {
      if (o.customer) customers.add(o.customer.trim());
      if (o.silk_color) silks.add(o.silk_color.trim());
      for (const it of o.order_items || []) {
        if (it.silk_color) silks.add(it.silk_color.trim());
        if (it.sku) skus.add(it.sku.trim());
        for (const cb of it.color_breakdown || []) if (cb.color) cupColors.add(cb.color.trim());
      }
    }
    const sort = (s) => Array.from(s).sort((a, b) => a.localeCompare(b, "pt-BR"));
    return { customers: sort(customers), silks: sort(silks), cupColors: sort(cupColors), skus: sort(skus) };
  }, [orders]);

  const set = (key, value) => setF((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="glass w-full max-w-lg p-6 sm:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kanban-filter-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FunnelSimple size={18} weight="bold" />
            <div className="overline">FILTRO AVANÇADO</div>
          </div>
          <button onClick={onClose} className="hover:text-red-500" data-testid="kanban-filter-close"><X size={20} /></button>
        </div>
        <h2 className="font-head text-2xl font-black mb-2">Filtrar pedidos</h2>
        <p className="text-sm text-zinc-600 mb-5">Os filtros valem para todas as colunas do Kanban. As opções são preenchidas automaticamente com base nos pedidos ativos.</p>

        <div className="space-y-4">
          <div>
            <label className="label">Cliente</label>
            <input
              className="input"
              list="kanban-filter-customers"
              placeholder="Digite ou escolha um cliente..."
              value={f.customer}
              onChange={(e) => set("customer", e.target.value)}
              data-testid="kanban-filter-customer"
            />
            <datalist id="kanban-filter-customers">
              {opts.customers.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Cor do silk</label>
              <select className="input" value={f.silk} onChange={(e) => set("silk", e.target.value)} data-testid="kanban-filter-silk">
                <option value="">Todas</option>
                {opts.silks.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Cor dos copos</label>
              <select className="input" value={f.cupColor} onChange={(e) => set("cupColor", e.target.value)} data-testid="kanban-filter-cup-color">
                <option value="">Todas</option>
                {opts.cupColors.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="label">SKU / Produto</label>
            <input
              className="input font-mono"
              list="kanban-filter-skus"
              placeholder="Digite ou escolha um SKU..."
              value={f.sku}
              onChange={(e) => set("sku", e.target.value)}
              data-testid="kanban-filter-sku"
            />
            <datalist id="kanban-filter-skus">
              {opts.skus.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>

          <div>
            <label className="label">Data limite de entrega</label>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                className="input font-mono"
                value={f.deadlineStart}
                onChange={(e) => set("deadlineStart", e.target.value)}
                data-testid="kanban-filter-deadline-start"
                aria-label="Data limite inicial"
              />
              <input
                type="date"
                className="input font-mono"
                value={f.deadlineEnd}
                onChange={(e) => set("deadlineEnd", e.target.value)}
                data-testid="kanban-filter-deadline-end"
                aria-label="Data limite final"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <label className="flex items-center gap-2 border border-zinc-300 px-3 py-2.5 cursor-pointer hover:border-black transition-colors">
              <input
                type="checkbox"
                className="w-4 h-4 accent-black"
                checked={f.overdueOnly}
                onChange={(e) => set("overdueOnly", e.target.checked)}
                data-testid="kanban-filter-overdue"
              />
              <span className="text-xs font-bold uppercase tracking-widest">Somente atrasados</span>
            </label>
            <div>
              <label className="label">Parado há mais de (dias)</label>
              <input
                className="input font-mono"
                type="number"
                min="0"
                placeholder="ex: 3"
                value={f.stoppedDays}
                onChange={(e) => set("stoppedDays", e.target.value.replace(/\D/g, ""))}
                data-testid="kanban-filter-stopped-days"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-6">
          <button
            type="button"
            className="btn btn-ghost flex-1"
            onClick={() => { setF({ ...EMPTY_FILTERS }); onApply({ ...EMPTY_FILTERS }); }}
            data-testid="kanban-filter-clear"
          >
            Limpar tudo
          </button>
          <button type="button" className="btn flex-1" onClick={() => onApply(f)} data-testid="kanban-filter-apply">
            Aplicar filtros
          </button>
        </div>
      </div>
    </div>
  );
}
